/**
 * Spanish (es) translations for the END-USER app only.
 *
 * The admin app keeps its own, completely separate translations at
 * `apps/admin/src/i18n/locales/es.json` (different i18n instance, JSON format).
 * Do NOT add admin-side keys here, and vice versa — the two never overlap.
 */
export default {
  common: {
    save: 'Guardar',
    saving: 'Guardando...',
    cancel: 'Cancelar',
    close: 'Cerrar',
    back: 'Volver',
    retry: 'Reintentar',
    loading: 'Cargando...',
    confirm: 'Confirmar',
    continue: 'Continuar',
    notAvailable: 'No disponible',
    skipToContent: 'Saltar al contenido principal',
    all: 'Todo',
    other: 'Otro',
    processing: 'Procesando...',
    viewAll: 'Ver todos',
  },

  dates: {
    relative: {
      now: 'ahora mismo',
      yesterday: 'ayer',
      today: 'hoy',
      tomorrow: 'mañana',
      minuteAgo: 'hace 1 minuto',
      minutesAgo: 'hace {n} minutos',
      hourAgo: 'hace 1 hora',
      hoursAgo: 'hace {n} horas',
      daysAgo: 'hace {n} días',
      weekAgo: 'hace 1 semana',
      weeksAgo: 'hace {n} semanas',
      monthAgo: 'hace 1 mes',
      monthsAgo: 'hace {n} meses',
      inDays: 'en {n} días',
      inWeeks: 'en {n} semanas',
    },
    remaining: {
      finished: 'Finalizado',
      endsToday: 'Termina hoy',
      endsTomorrow: 'Termina mañana',
      daysLeft: 'Quedan {n} días',
      weeksLeft: 'Quedan {n} semanas',
      monthsLeft: 'Quedan {n} meses',
    },
  },

  errors: {
    login: 'Error al iniciar sesión',
    register: 'Error al registrarse',
    registerOwner: 'Error al registrar al titular',
    registerEmployee: 'Error al registrar al empleado',
    requestPasswordReset: 'Error al solicitar el restablecimiento',
    resetPassword: 'Error al restablecer la contraseña',
    updateProfile: 'Error al actualizar el perfil',
    loadPharmacy: 'Error al cargar la farmacia',
    nifRequiredOwner: 'El CIF/NIF es obligatorio para registrar al titular',
    activationCodeRequired: 'El código de activación es obligatorio para registrar al titular',
    nifRequiredEmployee: 'El CIF/NIF es obligatorio para registrar al empleado',
    noActiveSession: 'No hay sesión activa',
    loadTickets: 'Error al cargar los tickets',
    loadTicket: 'Error al cargar el ticket',
    loadTicketSummary: 'Error al cargar el resumen de tickets',
    loadRecentTickets: 'Error al cargar tickets recientes',
    loadTicketBatch: 'Error al cargar el lote de tickets',
    uploadTicket: 'Error al subir el ticket',
    noTicketCreated: 'No se creó ningún ticket',
    selectChallenge: 'Selecciona al menos un reto',
    ticketBatchFailed: 'No se pudo crear el lote de tickets. Inténtalo de nuevo.',
    loadChallenges: 'Error al cargar los retos',
    loadChallenge: 'Error al cargar el reto',
    loadBrands: 'Error al cargar las marcas',
    loadNotifications: 'Error al cargar notificaciones',
    updateNotification: 'Error al actualizar notificación',
    markNotificationRead: 'Error al marcar la notificación como leída',
    loadBalance: 'Error al cargar el saldo',
    loadSummary: 'Error al cargar el resumen',
    loadBreakdown: 'Error al cargar el desglose',
    loadContributions: 'Error al cargar las contribuciones',
    loadContributionsSummary: 'Error al cargar el resumen de contribuciones',
    loadOwnerSummary: 'Error al cargar el resumen del titular',
    loadContributorBreakdown: 'Error al cargar el desglose por contribuidor',
    loadOwnerMovements: 'Error al cargar los movimientos del titular',
    invalidBalanceResponse: 'Respuesta de saldo inválida',
    invalidOwnerSummaryResponse: 'Respuesta de resumen de titular inválida',
    invalidOwnerMovementsResponse: 'Respuesta de movimientos del titular inválida',
    loadApprovals: 'Error al cargar las solicitudes de aprobación',
    approveRequest: 'Error al aprobar la solicitud',
    rejectRequest: 'Error al rechazar la solicitud',
    gdprExport: 'Error al exportar los datos personales',
    gdprDelete: 'Error al ejecutar la eliminación de datos',
    withdrawalRemoved: 'Los abonos ya no se realizan dentro de la aplicación.',
    payoutExternal: 'La ejecución de pagos es externa',
    bankAccountRemoved: 'La gestión de cuenta bancaria ya no está disponible en esta aplicación.',
  },

  routes: {
    login: 'Iniciar Sesión',
    register: 'Registro',
    registerPending: 'Registro pendiente',
    forgotPassword: 'Recuperar Contraseña',
    terms: 'Términos y condiciones',
    legalBases: 'Bases legales',
    dashboard: 'Panel Principal',
    challenges: 'Retos',
    ticketStatus: 'Estado de Tickets',
    cashBalance: 'Saldo',
    profile: 'Perfil',
    notifications: 'Notificaciones',
    privacy: 'Privacidad',
    ownerDashboard: 'Panel del Propietario',
    employeeApprovals: 'Aprobaciones de empleados',
    notFound: 'Página no encontrada',
  },

  dashboard: {
    greeting: '¡Hola, {name}!',
    defaultUserName: 'Usuario',
    loading: 'Cargando dashboard...',
    pharmacyTotalEarnings: 'Ganancias totales de tu farmacia',
    yourContribution: 'Tu contribución',
    yourBalance: 'Tu Saldo Disponible',
    viewDetails: 'Ver detalles',
    pending: 'Pendiente',
    tickets: 'Tickets',
    activeChallenges: 'Retos activos',
    featuredChallenges: 'Retos destacados',
    noActiveChallenges: 'No hay retos activos',
    noActiveChallengesDesc: 'Los nuevos retos aparecerán aquí cuando estén disponibles',
    recentActivity: 'Actividad reciente',
    ticketFallback: 'Ticket',
    noRecentActivity: 'Sin actividad reciente',
    noRecentActivityDesc: 'Sube tu primer ticket para comenzar a ganar recompensas',
    exploreChallenges: 'Explorar retos',
  },

  language: {
    title: 'Idioma',
    spanish: 'Español',
    english: 'Inglés',
    updated: 'Idioma actualizado',
    updateError: 'No se pudo actualizar el idioma. Inténtalo de nuevo.',
  },

  nav: {
    account: 'Mi cuenta',
    home: 'Inicio',
    dashboard: 'Panel',
    challenges: 'Retos',
    tickets: 'Tickets',
    balance: 'Saldo',
    profile: 'Perfil',
    notifications: 'Avisos',
    privacy: 'Privacidad',
    approvals: 'Aprobaciones',
    mainNavigation: 'Navegación principal',
    navigation: 'Navegación',
    menu: 'Menú',
  },

  header: {
    rolePharmacist: 'Farmacéutico',
    roleOwner: 'Propietario',
    contact: 'Contáctanos',
    openContact: 'Abrir sección de contacto',
    userMenu: 'Menú de usuario',
    myProfile: 'Mi Perfil',
    logout: 'Cerrar Sesión',
    login: 'Iniciar Sesión',
    logoAlt: 'P&G Health Contigo Incentivos',
  },

  layout: {
    pageTitle: {
      Dashboard: 'Inicio',
      OwnerDashboard: 'Panel',
      Challenges: 'Retos',
      TicketStatus: 'Tickets',
      CashBalance: 'Saldo',
      Profile: 'Mi Perfil',
      Notifications: 'Notificaciones',
      EmployeeApprovals: 'Aprobaciones',
      Privacy: 'Privacidad',
    },
    logout: 'Cerrar sesión',
    mainAction: 'Acción principal',
    ticketsUploaded: 'Tickets subidos',
    ticketsUploadedDetail: 'Se han subido {count} ticket correctamente | Se han subido {count} tickets correctamente',
  },

  mobile: {
    upload: 'Subir',
    uploadTicketAria: 'Subir ticket de compra',
  },

  contact: {
    kicker: 'Contáctanos',
    heading: '¿Tienes alguna pregunta o necesitas asistencia?',
    text: 'Estamos aquí para ayudarte.',
    sendEmailAria: 'Enviar correo a {email}',
    copyEmail: 'Copiar correo',
    emailCopied: 'Correo copiado',
    responseTime: 'Nuestro equipo responderá a tu mensaje en un plazo de 24–48 horas hábiles.',
  },

  footer: {
    sectionLabel: 'Información legal y corporativa',
    legalLinks: 'Enlaces legales',
    myData: 'Mis Datos',
    terms: 'Términos y condiciones',
    privacy: 'Privacidad',
    cookies: 'Acerca de las cookies',
    copyright:
      '© 2025 Procter & Gamble. Todos los derechos reservados. El uso y acceso a la información de este sitio está sujeto a los términos y condiciones establecidos en nuestro acuerdo legal.',
  },

  status: {
    ticket: {
      approved: 'Aprobado',
      in_review: 'En revisión',
      declined: 'Rechazado',
    },
    challenge: {
      active: 'Activo',
      finalized: 'Finalizado',
    },
    withdrawal: {
      pending: 'Pendiente',
      processing: 'Procesando',
      completed: 'Completado',
      failed: 'Fallido',
    },
    generic: {
      success: 'Éxito',
      warning: 'Advertencia',
      error: 'Error',
      info: 'Info',
      pending: 'Pendiente',
      active: 'Activo',
      inactive: 'Inactivo',
    },
  },

  quickUpload: {
    title: 'Subir Ticket',
    takePhoto: 'Toca para hacer foto',
    fromGallery: 'o selecciona de la galería',
    previewAlt: 'Vista previa del ticket',
    removeImage: 'Eliminar imagen',
    selectChallenges: 'Selecciona los retos',
    selectedCount: '{count} seleccionados',
    noActiveChallenges: 'No hay retos activos en este momento',
    invalidImage: 'Por favor selecciona una imagen válida',
    imageTooLarge: 'La imagen es demasiado grande (máximo 10MB)',
    noTicketCreated: 'No se pudo crear ningún ticket para los retos seleccionados.',
    uploadError: 'Error al subir el ticket',
    processing: 'Procesando...',
    uploading: 'Subiendo ticket... {progress}%',
    potentialEarnings: 'Ganancia potencial',
    disclaimer: 'No se aceptan recibos de compras online. Solo tickets físicos de tienda.',
    submitUploading: 'Subiendo...',
    submit: 'Subir a {count} reto | Subir a {count} retos',
  },

  quickWithdraw: {
    title: 'Abono de fondos',
    requested: '¡Abono solicitado!',
    requestedDetail: 'Se procesará en 24-48 horas laborables',
    availableBalance: 'Saldo disponible',
    insufficientBalance: 'Saldo insuficiente',
    minToWithdraw: 'Necesitas al menos {amount} para abonar',
    howMuch: '¿Cuánto quieres abonar?',
    enterAmount: 'Introduce importe',
    minAmount: 'El importe mínimo es {amount}',
    exceedsBalance: 'El importe supera tu saldo disponible',
    selectedAmount: 'Importe seleccionado:',
    destinationAccount: 'Cuenta de destino',
    verifiedAccount: 'Cuenta verificada',
    noBankAccount: 'Configura tu cuenta bancaria en el perfil',
    infoNote: 'El abono se procesará en 24-48 horas laborables',
    withdrawError: 'Error al procesar el abono',
    submitProcessing: 'Procesando...',
    submit: 'Solicitar abono {amount}',
  },

  brand: {
    title: 'P&G Health Contigo',
    titleShort: 'P&G Health',
    subtitle: 'Programa de Retos para Farmacias',
  },

  challenges: {
    placeholderLabel: 'Reto',
    title: 'Retos disponibles',
    subtitle: 'Participa en los retos y gana recompensas subiendo tus tickets de compra',
    loadMore: 'Cargar más retos',
    showingCount: 'Mostrando {shown} de {total} retos',
    filter: {
      status: 'Estado',
      statusAll: 'Todos',
      statusActive: 'Activos',
      statusFinalized: 'Finalizados',
      brand: 'Marca',
      allBrands: 'Todas las marcas',
      search: 'Buscar',
      searchPlaceholder: 'Buscar retos...',
      clear: 'Limpiar filtros',
    },
    list: {
      empty: 'No hay retos disponibles',
      emptyDesc: 'Vuelve más tarde para ver nuevos retos',
    },
    card: {
      variableReward: 'Recompensa variable',
      lastDay: 'Último día',
      daysRemaining: '{days} días restantes',
      until: 'Hasta {date}',
      finishedOn: 'Finalizado {date}',
      uploadTicket: 'Subir ticket',
    },
    detail: {
      fallbackTitle: 'Detalle del reto',
      loading: 'Cargando detalle del reto...',
      imageAlt: 'Imagen del reto: {title}',
      reward: 'Recompensa',
      rewardPerTicket: 'Recompensa por ticket válido',
      description: 'Descripción',
      validityPeriod: 'Periodo de validez',
      maxParticipations: 'Participaciones máximas',
      perUser: '{count} por usuario',
      viewTerms: 'Ver términos y condiciones',
      opensNewWindow: '(se abre en nueva ventana)',
      notFound: 'No se encontró el reto',
    },
  },

  fields: {
    email: 'Correo electrónico',
    password: 'Contraseña',
  },

  validation: {
    emailRequired: 'El correo electrónico es obligatorio',
    emailInvalid: 'Introduce un correo electrónico válido',
    passwordRequired: 'La contraseña es obligatoria',
    passwordMin: 'La contraseña debe tener al menos 8 caracteres',
    passwordUpper: 'La contraseña debe contener al menos una mayúscula',
    passwordLower: 'La contraseña debe contener al menos una minúscula',
    passwordNumber: 'La contraseña debe contener al menos un número',
    confirmRequired: 'Confirma tu contraseña',
    passwordsMismatch: 'Las contraseñas no coinciden',
    codeRequired: 'El código es obligatorio',
    code6: 'El código debe tener 6 dígitos',
    nameRequired: 'El nombre es obligatorio',
    surnameRequired: 'Los apellidos son obligatorios',
    nifRequired: 'El CIF/NIF es obligatorio',
    nifInvalid: 'Introduce un CIF/NIF válido',
    termsRequired: 'Debes aceptar los términos y condiciones',
    strength: {
      minLength: 'Mínimo 8 caracteres',
      uppercase: 'Al menos una mayúscula',
      lowercase: 'Al menos una minúscula',
      number: 'Al menos un número',
    },
  },

  password: {
    weak: 'Débil',
    medium: 'Media',
    strong: 'Fuerte',
    new: 'Nueva contraseña',
    confirm: 'Confirmar contraseña',
    requirementsTitle: 'La contraseña debe contener:',
    req8: 'Al menos 8 caracteres',
    reqUpper: 'Una letra mayúscula',
    reqLower: 'Una letra minúscula',
    reqNumber: 'Un número',
  },

  auth: {
    login: {
      feature1: 'Participa en retos exclusivos',
      feature2: 'Gana recompensas por tus ventas',
      feature3: 'Consulta tu saldo en tiempo real',
      feature4: 'Programa EXCLUSIVO para farmacias que comparten datos sell-out con P&G',
      title: 'Iniciar sesión',
      welcome: 'Bienvenido de nuevo',
      sessionExpired: 'Tu sesión ha expirado. Por favor, inicia sesión de nuevo.',
      rememberMe: 'Recordarme',
      forgotPassword: '¿Olvidaste tu contraseña?',
      submit: 'Iniciar sesión',
      noAccount: '¿No tienes cuenta? ',
      registerHere: 'Regístrate aquí',
    },
    forgot: {
      subtitle: 'Recuperar contraseña',
      stepRequest: 'Solicitar',
      stepReset: 'Restablecer',
      resetComplete: 'Tu contraseña ha sido restablecida correctamente. Serás redirigido a la página de inicio de sesión.',
      goLogin: 'Ir a iniciar sesión',
      requestIntro: 'Introduce tu correo electrónico y te enviaremos un código para restablecer tu contraseña.',
      codeSent: 'Hemos enviado un código a tu correo electrónico. Revisa tu bandeja de entrada.',
      sendCode: 'Enviar código',
      resetIntroPre: 'Introduce el código de 6 dígitos que hemos enviado a',
      resetIntroPost: 'y tu nueva contraseña.',
      verificationCode: 'Código de verificación',
      resend: '¿No recibiste el código? Reenviar',
      submitReset: 'Restablecer contraseña',
    },
    activation: {
      title: 'Activa tu cuenta',
      methodEmail: 'Introduce el código que hemos enviado a tu correo electrónico',
      methodSms: 'Introduce el código que hemos enviado a tu teléfono móvil',
      methodCode: 'Introduce el código de activación que has recibido',
      codeLabel: 'Código de activación',
      codeRequired: 'El código de activación es obligatorio',
      submit: 'Activar cuenta',
    },
    register: {
      subtitle: 'Registro de titular o empleado/a de farmacia',
      type: 'Tipo de registro',
      roleEmployee: 'Empleado/a',
      roleOwner: 'Titular',
      name: 'Nombre *',
      surname: 'Apellidos *',
      email: 'Correo electrónico *',
      nif: 'CIF/NIF *',
      phone: 'Teléfono (opcional)',
      activationCode: 'Código de activación *',
      password: 'Contraseña *',
      confirmPassword: 'Confirmar contraseña *',
      submitOwner: 'Crear cuenta de titular',
      submitEmployee: 'Solicitar alta de empleado/a',
      haveAccount: '¿Ya tienes cuenta? ',
      loginHere: 'Inicia sesión',
      linkLegalBases: 'bases legales',
      linkTerms: 'términos y condiciones',
      linkHere: 'aquí',
      termsIntro: 'Reglas del programa: al registrarme en el programa de incentivos de P&G Health, confirmo que he leído y acepto las',
      termsAnd: 'y los',
      termsParagraph2: 'Procter & Gamble ESPAÑA SAU, en calidad de Responsable del tratamiento de datos, tratará tus datos personales para registrarte en esta página, interactuar con sus servicios y enviarte comunicaciones relevantes al programa.',
      termsParagraph3: 'Puedes darte de baja del programa en cualquier momento desde el apartado "Privacidad". Puedes consultar nuestra política de privacidad',
    },
    registerPending: {
      defaultMessage: '¡Tu perfil ha sido creado con éxito! Por favor, contacta con el titular de tu farmacia para que lo apruebe.',
      feature1: 'Registro de titular o empleado/a',
      feature2: 'Gana recompensas por tus ventas',
      feature3: 'Consulta tu saldo en tiempo real',
      title: 'Registro pendiente de aprobación',
      emailLabel: 'Correo registrado: {email}',
      backToRegister: 'Volver al registro',
      goLogin: 'Ir a iniciar sesión',
    },
  },

  balance: {
    title: 'Mi Saldo',
    subtitle: 'Consulta créditos y deducciones por pagos externos finalizados.',
    payoutInfo: 'La cantidad acumulada se abonará mensualmente a la cuenta de tu farmacia de 10 en 10€. Si no has llegado a acumular 10€ en la fecha del pago, la cantidad acumulada se guardará y se abonará el siguiente mes que hayas acumulado 10€.',
    contributionByEmployee: 'Contribución por empleado',
    movementHistory: 'Historial de movimientos',
    contributorFallback: 'Empleado',
    loading: 'Cargando saldo...',
    display: {
      pharmacyBalance: 'Saldo de la farmacia',
      yourEarnings: 'Tus ganancias',
      pharmacyTotalEarnings: 'Ganancias totales de tu farmacia',
      yourContribution: 'Tu contribución',
      pending: 'Pendiente',
      paid: 'Abonado',
      totalEarned: 'Total ganado',
      movementBreakdown: 'Desglose de movimientos',
      movements: '{count} movimientos',
      noEarnings: 'No hay retos con ganancias todavía',
      challengeFallback: 'Reto',
      breakdownTypes: {
        ticket_credit: 'Créditos por tickets',
        welcome_bonus: 'Bonificación de bienvenida',
        external_payout_deduction: 'Deducción por pago externo',
        adjustment: 'Ajustes',
        reversal: 'Reversiones',
      },
    },
    contributorBreakdown: {
      loading: 'Cargando desglose de contribución...',
      empty: 'No hay contribuciones registradas todavía.',
      contributor: 'Contribuidor',
      totalPending: 'Total pendiente',
      eligible: 'Abonable',
      remainder: 'Resto',
      inLastPayment: 'En el último pago',
      totalPaid: 'Total abonado',
      lastPayment: 'Último pago {date}',
      noPaymentsYet: 'Todavía no hay pagos',
      notInLastPayment: 'No formó parte del último pago',
    },
    summaryCards: {
      totalEarned: 'Total ganado',
      eligibleBalance: 'Saldo a abonar',
      carryover: 'Resto acumulado',
      availableBalance: 'Saldo disponible',
      totalPaid: 'Total abonado',
    },
    transactions: {
      loading: 'Cargando movimientos...',
      empty: 'No hay movimientos registrados todavía.',
      previous: 'Anterior',
      next: 'Siguiente',
      pageOf: 'Página {page} de {total}',
    },
    contributionTable: {
      searchLabel: 'Buscar empleado',
      searchPlaceholder: 'Buscar empleado...',
      searchAria: 'Buscar empleado por nombre',
      loadingAria: 'Cargando contribuciones',
      loadingList: 'Cargando datos de contribuciones...',
      mobileAria: 'Contribución por empleado en móvil',
      empty: 'No hay contribuciones registradas',
      ticketsApproved: '{count} tickets aprobados',
      lastActivity: 'Última actividad',
      employee: 'Empleado',
      ticketsApprovedHeader: 'Tickets aprobados',
      contribution: 'Contribución',
      summaryAria: 'Resumen de contribuciones',
      employees: 'Empleados',
      totalTickets: 'Total tickets',
      totalContributions: 'Total contribuciones',
    },
    withdrawal: {
      availableToWithdraw: 'Saldo disponible para abonar',
      invalidAmount: 'Introduce un importe válido',
      exceedsBalance: 'El importe supera tu saldo disponible',
      noBankAccount: 'Para solicitar un abono necesitas configurar una cuenta bancaria en tu perfil.',
      verifiedAccount: 'Cuenta verificada',
      amountLabel: 'Importe a abonar',
      minBtn: 'Mín (10€)',
      importantInfo: 'Información importante:',
      info1: 'El abono se procesará en 24-48 horas laborables',
      info2: 'Se generará una factura automáticamente',
      info3: 'El importe mínimo de abono es 10€',
      submit: 'Solicitar abono',
      confirmMessage: '¿Confirmas el abono de {amount} a tu cuenta bancaria registrada?',
      confirmHeader: 'Confirmar abono',
      confirmAccept: 'Sí, abonar',
    },
  },

  notifications: {
    title: 'Notificaciones',
    subtitle: 'Centro persistente de avisos y eventos de aprobación.',
    filters: 'Filtros',
    filterAll: 'Todas',
    filterUnread: 'No leídas',
    unreadCount: '{count} no leídas',
    list: 'Listado',
    loading: 'Cargando notificaciones...',
    empty: 'No hay notificaciones para mostrar.',
    markRead: 'Marcar como leída',
    loadMore: 'Cargar más',
  },

  ownerDashboard: {
    loading: 'Cargando panel de titular...',
    defaultPharmacyName: 'Tu farmacia',
    ownerBadge: 'TITULAR',
    availableBalance: 'SALDO DISPONIBLE',
    balanceDesc: 'Saldo actual de la farmacia con pagos externos y deducciones ya reflejados.',
    viewDetailAria: 'Ver detalle del saldo de la farmacia',
    viewDetail: 'Ver detalle',
    approvalsAria: 'Ver {count} aprobaciones pendientes',
    approvePending: 'Aprobar pendientes',
    notificationsAria: 'Ver {count} avisos sin leer',
    unreadNotices: 'Avisos sin leer',
  },

  approvals: {
    title: 'Aprobación de empleados',
    subtitle: 'Gestiona solicitudes de acceso pendientes para tu farmacia.',
    filterPending: 'Pendientes',
    filterApproved: 'Aprobadas',
    filterRejected: 'Rechazadas',
    filterAll: 'Todas',
    statusApproved: 'Aprobada',
    statusRejected: 'Rechazada',
    statusPending: 'Pendiente',
    filter: 'Filtro',
    requests: 'Solicitudes',
    pendingCount: '{count} pendientes',
    loading: 'Cargando solicitudes...',
    empty: 'No hay solicitudes para el filtro seleccionado.',
    employeeFallback: 'Empleado/a',
    requestedOn: 'Solicitado {date}',
    reason: 'Motivo: {reason}',
    rejectPlaceholder: 'Motivo de rechazo (opcional)',
    approve: 'Aprobar',
    reject: 'Rechazar',
  },

  tickets: {
    receiptPlaceholder: 'Recibo',
    view: {
      title: 'Mis Tickets',
      subtitle: 'Gestiona tus tickets de compra y revisa su estado',
      uploadTicket: 'Subir ticket',
      loading: 'Cargando tickets...',
      summaryTotal: 'Total',
      summaryApproved: 'Aprobados',
      summaryInReview: 'En revisión',
      summaryDeclined: 'Rechazados',
      loadMore: 'Cargar más',
    },
    list: {
      tabAll: 'Todos',
      tabApproved: 'Aprobados',
      tabInReview: 'En revisión',
      tabDeclined: 'Rechazados',
      loadingAria: 'Cargando tickets',
      loadingList: 'Cargando lista de tickets...',
      emptyAll: 'No tienes tickets todavía',
      emptyApproved: 'No tienes tickets aprobados',
      emptyInReview: 'No tienes tickets en revisión',
      emptyDeclined: 'No tienes tickets rechazados',
      ticketFallback: 'Ticket',
      processedOn: '• Procesado {date}',
      approved: 'Aprobado',
      declinedFallback: 'Ticket rechazado',
      pendingReview: 'Pendiente de revisión',
      thumbnailAlt: 'Ticket {id}',
      thumbnailUnavailable: 'Imagen no disponible para el ticket {id}',
      itemAria: 'Ticket {title}, estado: {status}',
      statusApproved: 'aprobado',
      statusDeclined: 'rechazado',
      statusInReview: 'en revisión',
    },
    upload: {
      uploadingFor: 'Subiendo ticket para:',
      selectFile: 'Seleccionar archivo',
      dragImage: 'Arrastra una imagen aquí',
      orUsePicker: 'o usa el selector para elegir un archivo',
      formats: 'Formatos: JPEG, PNG, WebP • Máx. {size}MB',
      noFileSelected: 'Ningún archivo seleccionado',
      invalidFormat: 'Formato no válido: {name}. Solo se permiten imágenes (JPEG, PNG, WebP).',
      fileTooLarge: 'Archivo demasiado grande: {name}. El tamaño máximo es {size}MB.',
      filesSelected: '{count} archivo(s) seleccionado(s)',
      processing: 'Procesando imagen...',
      uploading: 'Subiendo... {progress}%',
      previewTitle: 'Vista previa',
      previewAlt: 'Vista previa {index}',
    },
    uploadDialog: {
      header: 'Subir ticket',
      selectChallenges: 'Selecciona los retos',
      placeholder: 'Selecciona uno o varios retos',
      oneChallenge: '1 reto',
      challengesSelected: '{count} retos seleccionados',
      emptyState: 'Selecciona uno o varios retos para subir tu ticket',
      success: '¡Ticket enviado correctamente! Será revisado en breve.',
    },
    detail: {
      fallbackTitle: 'Detalle del ticket',
      batchId: 'ID de lote',
      batchCopied: 'Copiado al portapapeles',
      batchCopyAria: 'Copiar ID de lote al portapapeles',
      creditedAmount: 'Importe acreditado',
      declineReasonLabel: 'Motivo del rechazo:',
      declineReasonFallback: 'El ticket no cumple los requisitos de la campaña.',
      // Keys mirror review_reason_codes codes; texts match the decline email.
      declineReasons: {
        NOT_ELIGIBLE: 'No elegible',
        MISSING_INFORMATION: 'Información faltante',
        DUPLICATE_RECEIPT: 'Recibo duplicado',
        FRAUD_SUSPECTED: 'Fraude sospechado',
        NOT_READABLE: 'No se puede leer',
      },
      imageUnavailable: 'La imagen del ticket no está disponible en este momento.',
      history: 'Historial',
      uploaded: 'Ticket subido',
      approved: 'Ticket aprobado',
      declined: 'Ticket rechazado',
      inReview: 'En revisión',
      associatedChallenge: 'Reto asociado',
      uploadDate: 'Fecha de subida',
      processedDate: 'Fecha de procesado',
      notFound: 'No se encontró el ticket',
      imageAlt: 'Ticket {id}',
    },
  },

  profile: {
    title: 'Mi Perfil',
    subtitle: 'Gestiona tu información personal y datos de farmacia',
    loading: 'Cargando perfil...',
    loadError: 'Error al cargar el perfil',
    personalData: 'Datos personales',
    privacy: 'Privacidad',
    privacyDescription:
      'Accede a las funciones GDPR para exportación y eliminación de datos personales.',
    openPrivacy: 'Abrir privacidad',
    updateSuccess: 'Perfil actualizado correctamente',
    updateError: 'Error al guardar los cambios',
    pharmacyTab: 'Mi farmacia',
    pharmacyName: 'Nombre',
    pharmacyNif: 'CIF/NIF',
    pharmacyAddress: 'Dirección',
    pharmacyOwner: 'Propietario/a',
    pharmacyTitle: 'Mi Farmacia',
    pharmacyNotice: 'Para modificar los datos de la farmacia, contacta con el administrador.',
    session: 'Sesión',
    logout: 'Cerrar sesión',
  },

  bankAccount: {
    registeredTitle: 'Cuenta bancaria registrada',
    verified: 'Verificada',
    pending: 'Pendiente',
    holder: 'Titular',
    iban: 'IBAN',
    bic: 'BIC/SWIFT',
    editAccount: 'Modificar cuenta',
    setupInfo: 'Configura tu cuenta bancaria para poder recibir los abonos de tu saldo.',
    reenterIbanInfo: 'Por seguridad, vuelve a introducir el IBAN completo para guardar cambios.',
    holderName: 'Nombre del titular',
    holderNameHelp: 'Nombre completo como aparece en la cuenta',
    holderNameRequired: 'El nombre del titular es obligatorio',
    ibanRequired: 'El IBAN es obligatorio',
    ibanInvalid: 'El IBAN no es válido',
    ibanHelp: 'Formato: ES00 0000 0000 00 0000000000',
    bicOptional: 'BIC/SWIFT (opcional)',
    bicHelp: '8 u 11 caracteres, ej: CAIXESBBXXX',
    saveChanges: 'Guardar cambios',
    saveAccount: 'Guardar cuenta',
  },

  profileForm: {
    name: 'Nombre',
    surname: 'Apellidos',
    nif: 'NIF/NIE',
    nifReadonly: 'El NIF/NIE no se puede modificar',
    email: 'Email',
    phone: 'Teléfono',
    phoneFormat: 'Formato: 612 345 678',
    save: 'Guardar cambios',
    nameRequired: 'El nombre es obligatorio',
    nameMin: 'El nombre debe tener al menos 2 caracteres',
    nameLetters: 'El nombre solo puede contener letras',
    surnameRequired: 'Los apellidos son obligatorios',
    surnameMin: 'Los apellidos deben tener al menos 2 caracteres',
    surnameLetters: 'Los apellidos solo pueden contener letras',
    emailRequired: 'El email es obligatorio',
    emailInvalid: 'El email no es válido',
  },

  privacy: {
    title: 'Privacidad y GDPR',
    subtitle: 'Gestiona exportación de datos personales y solicitud de supresión.',
    exportSuccess: 'Exportación completada. Puedes copiar el resultado JSON.',
    exportError: 'No se pudo exportar la información.',
    eraseConfirmRequired: 'Escribe BORRAR para confirmar la eliminación irreversible.',
    eraseError: 'No se pudo ejecutar la eliminación de datos.',
    exportTitle: 'Exportar mis datos',
    exportDesc: 'Genera la exportación completa de tus datos personales almacenados en la plataforma.',
    generateExport: 'Generar exportación',
    eraseTitle: 'Solicitar eliminación',
    eraseDescPre: 'Esta acción es irreversible y anonimiza tus datos personales. Para confirmar, escribe',
    eraseKeyword: 'BORRAR',
    erasePlaceholder: 'Escribe BORRAR',
    eraseButton: 'Eliminar mis datos',
  },

  legal: {
    basesTitle: 'Bases legales',
    basesSubtitle: 'Bases legales del Programa de Incentivos P&G - Farmacias.',
    termsTitle: 'Términos y condiciones',
    termsSubtitle: 'Bases legales del Programa de Incentivos P&G - Farmacias.',
    backToRegister: 'Volver al registro',
  },

  notFound: {
    backHome: 'Volver al inicio',
    goLogin: 'Ir al login',
    title: 'Página no encontrada',
    description: 'La página que buscas no existe o ha sido movida. Verifica la URL o vuelve al inicio.',
    goBack: 'Volver atrás',
    needHelp: '¿Necesitas ayuda?',
    contactSupport: 'Contacta con soporte',
  },
} as const
